import { visibleTrend, trendEmptyMessage } from '../trendPeriods'
import { PartialPeriods } from './PartialPeriods'
import { useRequestState } from '../useRequestState'
import { TENANT } from '../tenant'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { X } from 'lucide-react'
import { InsightChart } from '../charts/InsightChart'
import { bucketRange, fetchToplistTrend, isoMinusDays, formatRangeLabel } from '../landing'
import { brand } from '../theme'
import type { Chart } from '../types'
import { InfoTip } from './InfoTip'
import { ShareBasisLine } from './ShareBasisLine'
import { DATA_SOURCE_NAME } from '../missingLabels'

// MP-OS-144 — Olof, twice: "det skulle kunna gå att trycka på den där och få pop-upen med
// tidskurvan". The top list shows one moment in time (CU, share, change vs the previous
// period), so a row that moved 356 % reads the same whether it is a trend or a one-off spike.
// This answers "which is it?" without leaving the view and rebuilding the Overview filters.
//
// End at the actual list data date, not the unrelated selected month for week/year lists.
// Clamp the start to the published history boundary; never imply older data is present.
const WEEKS = 52

export function TrendPopup({
  month,
  sku,
  brandName,
  title,
  segment, area, leaf,
  dataThrough,
  historyFrom,
  onClose,
}: {
  month: string // 'YYYY-MM' — the list's period
  sku?: string
  brandName?: string
  title: string
  segment?: string
  area?: string
  leaf?: string
  dataThrough?: string
  historyFrom?: string
  onClose: () => void
}) {
  const [attempt, setAttempt] = useState(0)
  const [showPartial, setShowPartial] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const to = dataThrough ?? bucketRange(month, 'month')?.to ?? month
  const requestedFrom = isoMinusDays(to, WEEKS * 7 - 1)
  const from = historyFrom && historyFrom > requestedFrom && historyFrom <= to ? historyFrom : requestedFrom
  const requestKey = JSON.stringify([TENANT.manufacturer, from, to, sku, brandName, segment, area, leaf, attempt])
  const [cu, setCu] = useRequestState<Chart | null>(requestKey, null)
  const [share, setShare] = useRequestState<Chart | null>(requestKey, null)
  const [state, setState] = useRequestState<'loading' | 'ready' | 'error'>(requestKey, 'loading')
  const [coverage, setCoverage] = useRequestState<{ first: string | null; last: string | null } | null>(requestKey, null)
  // MP-OS-207: the API's note says what a 0 and a gap mean for this curve (verified zeros vs missing days).
  const [note, setNote] = useRequestState<string | null>(requestKey, null)

  useEffect(() => {
    let cancelled = false
    setState('loading')
    const opts = {
      from,
      to,
      sku,
      brand: brandName,
      segment, area, leaf,
    }
    fetchToplistTrend(opts)
      .then(result => {
        if (cancelled) return
        setCu(result.cu)
        setShare(result.share)
        setCoverage(result.coverage)
        setNote(typeof result.note === 'string' && result.note.trim() ? result.note : null)
        setState('ready') // A successful empty source is not a request failure.
      })
      .catch(() => !cancelled && setState('error'))
    return () => {
      cancelled = true
    }
  }, [from, to, sku, brandName, segment, area, leaf, attempt])

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => { if (previous?.isConnected) previous.focus() }
  }, [])

  return (
    <div style={S.backdrop} onClick={onClose} role="presentation">
      <div
        style={S.modal}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} over time`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onClose() }
          if (e.key !== 'Tab') return
          const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex="0"]',
          ) ?? [])
          const first = controls[0], last = controls[controls.length - 1]
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
        }}
      >
        <div style={S.head}>
          <div>
            <div style={S.title}>{title}</div>
            <div style={S.sub}>{formatRangeLabel(from, to)}{segment ? ` · ${segment}` : ''}</div>
          </div>
          <button ref={closeRef} style={S.close} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {state === 'loading' && <div style={S.empty}>Loading…</div>}
        {state === 'error' && <div style={S.empty} role="alert">Could not load the curve. <button onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>}
        {state === 'ready' && (
          <div style={S.charts}>
            {/* MPD-64: the coverage stays visible (freshness); the source note is in the ⓘ. */}
            <div style={S.sub}>
              {coverage?.first && coverage.last ? `Sales records: ${formatRangeLabel(coverage.first, coverage.last)}` : 'No sales records for this selection.'}
              <InfoTip label="About the source" size="small">
                {`Source: ${DATA_SOURCE_NAME}, daily sales.`} CU and share use the same selection.{' '}
                {note ?? 'A gap in the curve is not a confirmed zero.'}
              </InfoTip>
            </div>
            <PartialPeriods checked={showPartial} onChange={setShowPartial} />
            <section>
              <div style={S.chartTitle}>CU sold over time</div>
              {cu ? <InsightChart chart={visibleTrend(cu, showPartial)!} animate={false} emptyMessage={trendEmptyMessage(cu, showPartial, "No sales recorded in this window.")} />
                  : <div style={S.empty}>No sales recorded in this window.</div>}
            </section>
            <section>
              <div style={S.chartTitle}>Market share · {share?.denominator ?? segment ?? 'Whole market'}</div>
              {share?.share_basis && <ShareBasisLine basis={share.share_basis} style={{ ...S.sub, margin: 0 }} />}
              {share ? <InsightChart chart={visibleTrend(share, showPartial)!} animate={false} emptyMessage={trendEmptyMessage(share, showPartial, "No share data in this window.")} />
                     : <div style={S.empty}>No share data in this window.</div>}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

const S: Record<string, CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(15,32,32,0.45)', zIndex: 60,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    background: brand.surface, border: `1px solid ${brand.grey}`, borderRadius: 14,
    width: 'min(880px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: '16px 18px 18px',
    boxShadow: '0 18px 48px rgba(0,0,0,0.22)', fontFamily: brand.font,
  },
  head: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 },
  title: { fontSize: 15, fontWeight: 700, color: brand.ink },
  sub: { fontSize: 11.5, color: brand.greyText, marginTop: 2 },
  close: { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: brand.greyText, padding: 4 },
  charts: { display: 'grid', gap: 14 },
  chartTitle: { fontSize: 12.5, fontWeight: 700, color: brand.greyText, marginBottom: 4 },
  empty: { padding: '32px 0', textAlign: 'center', color: brand.greyText, fontSize: 13 },
}
